import { ExportPdfModal } from "../LegalFiscalReport/components/ExportPdfModal";
import { useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect, useCallback, useRef, cloneElement, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { TERMS_URL } from "../../../Components/TermsConsent";
import {
  ReportPageContainer,
  ReportWrapper,
  ReportHeader,
  HeaderDivider,
  SectionWrapper,
  PdfExclusionNotice,
  PdfExclusionText,
  PdfExclusionAction,
  BackToSummaryButton,
  SectionHeaderRow,
  SectionHeaderIconBadge,
  SectionHeaderOverline,
  SectionHeaderTitle,
  SectionHeaderMetaRow,
  SectionStatusPill,
  SectionFiabTag,
  SectionLoadingState,
  Spinner,
  Disclaimer,
  DisclaimerLink,
  LockedSectionCard,
  TierLockedCard,
  TierLockedIcon,
  TierLockedTitle,
  TierLockedText,
  TierLockedAvailability,
  LockedSectionIcon,
  LockedSectionText,
  LockedSectionCTA,
  BlurredContentWrapper,
  LockedBlockRelative,
  LockedSectionRelative,
  LockedOverlay,
  CockpitHeaderRow,
  CockpitHeaderLeft,
  CockpitBadge,
  CockpitBadgeDot,
  CockpitTitle,
  CockpitSubtitle,
  CockpitHeaderRight,
  CockpitOverline,
  CockpitRef,
  CockpitDate,
  SectionSources,
} from "@dd-report-components/report/Styles";
import type { DueDiligenceCase, DueDiligenceTier } from "../../../Common/Types-DTOs/DueDiligence";
import { DueDiligenceCaseStatuses, dueDiligenceTierSchema } from "../../../Common/Types-DTOs/DueDiligence";
import { ReportSubheader } from "../LegalFiscalReport/components/ReportSubheader";
import { PropertyMap } from "../LegalFiscalReport/components/PropertyMap";
import { SectionErrorBoundary } from "../LegalFiscalReport/components/SectionErrorBoundary";
import { SECTION_GROUP_KEYS, sectionLabel } from "@dd-report-components/report/Sections";
import { BlockRenderer, renderWithFileRefs, renderSynthStrip as renderSynthStripBase, renderSectionCallouts } from "@dd-report-components/report/BlockRenderer";
import { reportEventBus } from "@dd-report-components/report/ReportEvents";
import { ExecutiveSummaryCockpit } from "@dd-report-components/report/ExecutiveSummaryCockpit";
import { HousingSimulator } from "../LegalFiscalReport/components/HousingSimulator";
import { ValuationSimulator } from "../LegalFiscalReport/components/ValuationSimulator";
import {
  CitationSidePanel,
  CitationsContext,
  type CitationsMap
} from "../LegalFiscalReport/components/CitationSidePanel";
import { NormaModal } from "../LegalFiscalReport/components/NormaModal";
import {
  SectionNotesMarker,
  useMarkerWake,
  ReportNotesPanel,
  ReportNotesContextMenu,
  type NotesMenuAnchor,
} from "../LegalFiscalReport/components/ReportNotes";
import {
  ReportOutline,
  ReportCaseMeta,
  LEGAL_FISCAL_SECTION_ICONS,
  ORIENTATIVE_SECTION_NUMBERS,
  NO_STATUS_SECTION_NUMBERS,
  designSectionStatus,
  isSectionAwaitingData,
  SectionDataStatuses,
} from "../LegalFiscalReport/components/ReportSectionNav";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { useDueDiligenceReportNotes } from "../../../Common/Hooks/useDueDiligenceReportNotes";
import { useHiddenPdfSections } from "../../../Common/Hooks/useHiddenPdfSections";
import { useDueDiligencePayment } from "../../../Common/Hooks/useDueDiligence/useDueDiligencePayment";
import { useBusiness } from "../../../Common/Hooks/useBusiness";
import { UpsellModal } from "../Components/UpsellModal";
import { DueDiligenceCreditCosts } from "../NewCase/PaymentSection";
import { useDueDiligenceFeedbackGate } from "../../../Common/Hooks/useDueDiligenceFeedbackGate";
import { useCanonicalCaseUrl } from "../../../Common/Hooks/useCanonicalCaseUrl";
import { useReportAnchorNavigation } from "../../../Common/Hooks/useReportAnchorNavigation";
import { FeedbackGateOverlay } from "../../../Components/DueDiligenceFeedback/FeedbackGateOverlay";
import { FeedbackFooterPrompt } from "../../../Components/DueDiligenceFeedback/FeedbackFooterPrompt";
import { isErr } from "../../../Common/Utils/Result";
import { formatLongDate } from "../../../Common/Utils/dateUtils";
import { blockDomId } from "../../../Common/Utils/reportAnchor";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import * as Sentry from "@sentry/react";
import {
  FiLock,
  FiUnlock,
  FiChevronLeft,
  FiCheck,
  FiAlertTriangle,
  FiAlertCircle,
  FiFileText,
  FiEyeOff,
} from "react-icons/fi";

const DEFAULT_RETRY_SECONDS = 15;
const ERROR_RETRY_SECONDS = 10;
// Per-section retry chain: while a section is still pending, re-request it on this
// cadence. The chain lives in a ref so the viewport re-entering a section never
// resets an in-flight countdown. Pre-payment data lands fast (cadastral fetch), so
// we poll tighter there; paid sections run heavier tasks, so a slower cadence.
const SECTION_RETRY_SECONDS_PREPAID = 5;
const SECTION_RETRY_SECONDS_PAID = 30;

type FetchState = "loading" | "ready";

const sectionDomId = (number: number) => `section-${number}`;

const REPORT_TITLE_KEY = "dueDiligence.finalReport.reportTitles.final";

// Preview visibility by section number (pending_payment state):
//  · Section 1   → the cockpit (what the case knows + the map), then the CTA.
//  · Section 2   → visible: every pre-payment source lands in it (the cadastral
//                  record, the views, the energy and habitability certificates).
//  · 3-12        → blur + lock overlay until payment: no pre-payment source
//                  reaches them, so the seed holds only what each block will
//                  cover, and showing it plain would be a bare line with no CTA.
//  · Section 13  → the disclaimer: written before payment, so it falls through
//                  to the default branch (open).
const PREVIEW_VISIBLE_SECTIONS = new Set([2]);
const PREVIEW_BLUR_SECTIONS = new Set([3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

// Placeholder citations for the housing simulator's <CiteSup> markers — no
// backend citations pipeline exists yet (see project_mike308_kpi_backend_todo
// memory). Real wiring will resolve these the same way `filesMap` resolves
// {{file:...}} chips, keyed by whatever the report's own citation table uses.
const DEMO_CITATIONS: CitationsMap = {
  cdc: {
    tag: "01",
    title: "Certificación de Dominio y Cargas",
    detail: "Registro de la Propiedad de Torrent nº 3 · finca 54.460 · expedida el 28 jul 2026. Libertad de cargas con valor probatorio frente a tercero (art. 225 LH).",
    where: "Expediente registral · Anexo — Tabla de citas",
    primary: { kind: "pdf", url: "/sample-report/assets/01-certificacion-dominio-cargas.pdf", label: "Certificación de Dominio y Cargas (PDF)" },
  },
  // The simulator's legacy `cat` key points to the value-reference certificate.
  // Fiscal prose cites the descriptive cadastral document separately.
  fiscal_cat: {
    tag: "03",
    title: "Consulta Descriptiva y Gráfica (Catastro)",
    detail: "Sede Electrónica del Catastro · ref. 8789712YJ1688N0001MB · consulta de 21 jul 2026. Clase urbana, uso «solar», 1.630 m².",
    where: "Sede del Catastro · Anexo — Tabla de citas",
    primary: { kind: "pdf", url: "/sample-report/assets/03-catastro-consulta-descriptiva.pdf", label: "Consulta Descriptiva y Gráfica (PDF)" },
  },
  vref: {
    tag: "04",
    title: "Certificación de inexistencia de valor de referencia",
    detail: "Certificación catastral de 21 jul 2026: «No existe valor de referencia» para el solar; la base imponible se rige por el valor de mercado o declarado.",
    where: "Sede del Catastro · Anexo — Tabla de citas",
    primary: { kind: "pdf", url: "/sample-report/assets/certificado-inexistencia-valor-referencia.pdf", label: "Certificado de inexistencia de valor de referencia (PDF)" },
  },
  eco: {
    tag: "08",
    title: "Orden ECO/805/2003",
    detail: "Normas de valoración de bienes inmuebles: método residual estático (art. 42). Base metodológica de la valoración orientativa del suelo.",
    where: "Normativa · Anexo — Tabla de citas",
    primary: { kind: "link", url: "https://www.boe.es/buscar/act.php?id=BOE-A-2003-7253", label: "Orden ECO/805/2003 · BOE (web oficial)" },
  },
  rm: {
    tag: "09",
    title: "Registro Mercantil de Valencia",
    detail: "Certificación / nota del RM sobre la representación vigente de Promociones Jobajal SL (art. 98 Ley 24/2001) y verificación mercantil nominal.",
    where: "Registro Mercantil · Anexo — Tabla de citas",
    primary: { kind: "link", url: "https://sede.registradores.org/", label: "Sede del Colegio de Registradores (web oficial)" },
  },
  cat: {
    tag: "03",
    title: "Certificado de inexistencia de valor de referencia",
    detail: "Certificación catastral: «No existe valor de referencia» para la finca; la base imponible se rige por el valor de mercado o declarado.",
    where: "Sede del Catastro · Anexo — Tabla de citas",
    primary: { kind: "pdf", url: "/sample-report/assets/certificado-inexistencia-valor-referencia.pdf", label: "Certificado de inexistencia de valor de referencia (PDF)" },
    secondary: { kind: "link", url: "https://www1.sedecatastro.gob.es/", label: "Sede Electrónica del Catastro (web oficial)" },
  },
  urb: {
    tag: "15",
    title: "Informe urbanístico municipal · Exp. 29249/2026",
    detail: "Informe urbanístico del Ayuntamiento de Torrent (Exp. 29249/2026), firmado por el arquitecto municipal el 01/09/2026 y recibido por comparecencia en sede el 02/09/2026, con el plano de ordenación pormenorizada 7.24: confirma nº de plantas (PB+5), cornisa 18 m, ocupación por alineaciones (sin fondo máximo), 1 plaza de aparcamiento por vivienda (posible modif. nº 28 y reserva en radio 200 m) y disponibilidad de servicios. Una notificación previa fue rechazada el 18/08/2026 y el informe se reemitió el 01/09/2026.",
    where: "Informe urbanístico · plano 7.24 · Anexo — Tabla de citas",
    primary: { kind: "pdf", url: "/sample-report/assets/15-informe-urbanistico-29249-2026.pdf", label: "Informe urbanístico municipal · Exp. 29249/2026 (PDF)" },
    secondary: { kind: "pdf", url: "/sample-report/assets/07-geourbanismo.pdf", label: "Ficha GeoUrbanismo (PDF)" },
  },
  inf: {
    tag: "17",
    title: "Informe de inexistencia de infracción urbanística · Exp. 29972/2026",
    detail: "Informe del Ayuntamiento de Torrent (Exp. 29972/2026, firmado el 27/08/2026 y recibido por comparecencia en sede el 02/09/2026): no consta expediente de protección de la legalidad urbanística ni expediente sancionador por infracción sobre la finca.",
    where: "Informe municipal · Anexo — Tabla de citas",
    primary: { kind: "pdf", url: "/sample-report/assets/17-informe-inexistencia-infraccion-29972-2026.pdf", label: "Informe de inexistencia de infracción · Exp. 29972/2026 (PDF)" },
  },
  pgou: {
    tag: "05",
    title: "PGOU · Normas Urbanísticas",
    detail: "Plan General de Ordenación Urbana: parámetros edificatorios y condición de solar. No fija densidad ni tamaño mínimo de vivienda.",
    where: "Planeamiento municipal · Anexo — Tabla de citas",
  },
  hab: {
    tag: "16",
    title: "Habitabilidad · DC-09 y Decreto 80/2023",
    detail: "Normativa autonómica de habitabilidad: superficie útil mínima de vivienda 30 m² (24 m² el apartamento). Orden de 7/12/2009 (DC-09) y Decreto 80/2023 del Consell (DOGV). No es parámetro del PGOU.",
    where: "DOGV · cita legal externa",
    primary: { kind: "link", url: "https://dogv.gva.es/", label: "DOGV · normativa de habitabilidad DC-09 (web oficial)" },
  },
};

type ReportFileEntry = { publicName: string | null; fileName: string; signedUrl: string };

const resolveDemoAssetUrl = (
  files: Record<string, ReportFileEntry>,
  fileName: string,
  fallback: string,
): string => {
  const entry = Object.values(files).find((file) => file.fileName === fileName || file.publicName === fileName);
  return entry?.signedUrl || fallback;
};

const buildDemoCitations = (files: Record<string, ReportFileEntry>): CitationsMap => {
  const citations = { ...DEMO_CITATIONS };
  const withPrimary = (key: string, fileName: string) => {
    const citation = citations[key];
    if (!citation?.primary || citation.primary.kind !== "pdf") return;
    citations[key] = {
      ...citation,
      primary: {
        ...citation.primary,
        url: resolveDemoAssetUrl(files, fileName, citation.primary.url),
      },
    };
  };
  withPrimary("cdc", "01-certificacion-dominio-cargas.pdf");
  withPrimary("fiscal_cat", "03-catastro-consulta-descriptiva.pdf");
  withPrimary("vref", "certificado-inexistencia-valor-referencia.pdf");
  withPrimary("cat", "certificado-inexistencia-valor-referencia.pdf");
  withPrimary("urb", "15-informe-urbanistico-29249-2026.pdf");
  withPrimary("inf", "17-informe-inexistencia-infraccion-29972-2026.pdf");

  const urban = citations.urb;
  if (urban?.secondary?.kind === "pdf") {
    citations.urb = {
      ...urban,
      secondary: {
        ...urban.secondary,
        url: resolveDemoAssetUrl(files, "07-geourbanismo.pdf", urban.secondary.url),
      },
    };
  }
  return citations;
};

// Citations authored in the report itself (meta.citations) win over the demo
// map: the keys are the report's own annexes. A source url may be a
// {{file:<name>}} token, resolved against the case's uploaded documents the
// same way the inline chips are.
const FILE_TOKEN_RE = /^\{\{file:([^}]+)\}\}$/;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildReportCitations = (meta: any, files: Record<string, ReportFileEntry>): CitationsMap => {
  const authored = (meta?.citations || {}) as CitationsMap;
  const resolveSource = (source?: CitationsMap[string]["primary"]) => {
    const token = source?.url?.match(FILE_TOKEN_RE);
    if (!token) return source;
    // The seed rewrites {{file:<name>}} to {{file:<document id>}} across the
    // whole report, so the token is normally a key of the files map; the
    // by-name lookup stays for reports whose tokens were never rewritten.
    const ref = token[1];
    return { ...source!, url: files[ref]?.signedUrl ?? resolveDemoAssetUrl(files, ref, source!.url) };
  };
  const resolved: CitationsMap = {};
  for (const [key, citation] of Object.entries(authored)) {
    resolved[key] = {
      ...citation,
      ...(citation.primary && { primary: resolveSource(citation.primary)! }),
      ...(citation.secondary && { secondary: resolveSource(citation.secondary)! }),
    };
  }
  return { ...buildDemoCitations(files), ...resolved };
};

const readSelectionInside = (container: HTMLElement | null) => {
  if (!container) return null;
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
  const text = selection.toString().trim();
  if (!text) return null;
  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  return { text, rect: range.getBoundingClientRect() };
};

export const FinalReport = () => {
  const { t } = useTranslation();
  // A portfolio of plots carries several references ("A y B"): plural label,
  // the last one joined the way the reader's language does.
  const cadastralRefsText = (value: string) => {
    const refs = value.split(/\s+(?:y|and|·)\s+|\s*[,;]\s*/).filter(Boolean);
    if (refs.length < 2) return `${t("dueDiligence.finalReport.cockpit.cadastralRefLabel")} ${value}`;
    const list = refs.slice(0, -1).join(", ") + t("dueDiligence.finalReport.cockpit.cadastralRefsLastJoiner") + refs[refs.length - 1];
    return `${t("dueDiligence.finalReport.cockpit.cadastralRefsLabel")} ${list}`;
  };
  const { caseAlias } = useParams<{ caseAlias: string }>();
  const [notesSectionNumber, setNotesSectionNumber] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const notesDeepLinkDone = useRef(false);
  const [notesMenuAnchor, setNotesMenuAnchor] = useState<NotesMenuAnchor | null>(null);
  const { markersAwake, wakeMarkers } = useMarkerWake();
  const reportWrapperRef = useRef<HTMLDivElement>(null);
  const { getCase, getReportManifest, getReportNumberedSection, getReportFiles, exportPdf, getDueDiligencePricing, translateReport } = useDueDiligence();

  const [ddCase, setDdCase] = useState<DueDiligenceCase | null>(null);
  // MIKE-232: null = original (Spanish).
  const [language, setLanguage] = useState<string | null>(null);
  const {
    feedbackStatus,
    feedbackConfig,
    isFeedbackModalOpen,
    isSubmittingFeedback,
    initialNpsScore,
    openFeedbackModalWithScore,
    closeFeedbackModal,
    handleSubmitFeedback,
    handleDismissFeedbackPrompt,
  } = useDueDiligenceFeedbackGate(caseAlias, ddCase?.status === DueDiligenceCaseStatuses.completed);

  useCanonicalCaseUrl(caseAlias, ddCase?.alias);
  const [state, setState] = useState<FetchState>("loading");
  const [refetchKey, setRefetchKey] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [hasFailedAtLeastOnce, setHasFailedAtLeastOnce] = useState(false);
  // Manifest = cheap structural view (cover meta + per-section number/title/status
  // + block outline). Section content (heavy blocks) is fetched lazily per number.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [manifest, setManifest] = useState<any | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sectionContent, setSectionContent] = useState<Record<string, any>>({});
  const [activeSectionNumber, setActiveSectionNumber] = useState<number>(1);
  const sentryReportedRef = useRef(false);
  const renderErrorRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSilentRefetchRef = useRef(false);
  const {
    hiddenSections: excludedPdfSections,
    setHiddenSections: setExcludedPdfSections,
    initialize: initializeExcludedPdfSections,
  } = useHiddenPdfSections(caseAlias);
  const [clientPreview, setClientPreview] = useState(false);
  const isPreviewSectionVisible = useCallback((number: string | number) =>
    !clientPreview || !excludedPdfSections.includes(String(number)), [clientPreview, excludedPdfSections]);

  useEffect(() => {
    setClientPreview(false);
    setShowExportModal(false);
  }, [caseAlias]);

  const [enabledSections, setEnabledSections] = useState<Record<string, boolean>>({});
  const [enabledBlocks, setEnabledBlocks] = useState<Record<string, boolean>>({});
  const [filesMap, setFilesMap] = useState<Record<string, { publicName: string | null; fileName: string; signedUrl: string }>>({});
  const demoCitations = useMemo(() => buildReportCitations(manifest?.meta, filesMap), [manifest, filesMap]);
  const [pricing, setPricing] = useState<{ basic: number | null; professional: number | null; premium: number | null } | null>(null);
  const [creditCosts, setCreditCosts] = useState<DueDiligenceCreditCosts | null>(null);
  /** The tier the upgrade modal opens on; null while it's closed. */
  const [upsellTier, setUpsellTier] = useState<DueDiligenceTier | null>(null);
  // Subscribers upgrade with credits, so the header prices upgrades in them
  const { business } = useBusiness();
  const isSubscriber = Boolean(business?.credits?.dueDiligencePlan);

  const blockRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Lazy-loading machinery (kept in refs so the retry timers always see the
  // latest without re-subscribing):
  //  · retryTimersRef: active per-section retry chain. Its presence means a
  //    countdown is already armed.
  //  · inFlightRef: sections with a request in progress (dedup concurrent fetches).
  const retryTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const inFlightRef = useRef<Set<string>>(new Set());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sectionContentRef = useRef<Record<string, any>>({});
  useEffect(() => { sectionContentRef.current = sectionContent; }, [sectionContent]);
  const filesMapRef = useRef(filesMap);
  useEffect(() => { filesMapRef.current = filesMap; }, [filesMap]);
  const isPaidRef = useRef(false); // read by the retry chain to pick its cadence
  const ensureSectionRef = useRef<(n: string) => void>(() => {});
  const fetchSectionRef = useRef<(n: string) => void>(() => {});

  const getCaseRef = useRef(getCase);
  useEffect(() => { getCaseRef.current = getCase; });

  const getReportManifestRef = useRef(getReportManifest);
  useEffect(() => { getReportManifestRef.current = getReportManifest; });
  const getReportNumberedSectionRef = useRef(getReportNumberedSection);
  useEffect(() => { getReportNumberedSectionRef.current = getReportNumberedSection; });

  const caseStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!caseAlias) return;
    let cancelled = false;
    getCaseRef.current(caseAlias).then((result) => {
      if (cancelled || isErr(result)) return;
      const c = result.value.case;
      caseStatusRef.current = c.status;
      setDdCase(c);
      if (c.status === DueDiligenceCaseStatuses.pending_payment) {
        setState("ready");
      }
    });
    return () => { cancelled = true; };
  }, [caseAlias]);

  // Fetch manifest
  useEffect(() => {
    if (!caseAlias) return;
    let cancelled = false;
    const retryTimerRef: { current: ReturnType<typeof setTimeout> | null } = { current: null };

    const silent = isSilentRefetchRef.current;
    isSilentRefetchRef.current = false;
    if (!silent) {
      setState("loading");
      setManifest(null);
      setSectionContent({});
      setHasFailedAtLeastOnce(false);
      sentryReportedRef.current = false;
    }

    const reportErrorToSentry = (
      title: string,
      data: { status?: number; errorCode?: string; traceId?: string },
    ) => {
      if (sentryReportedRef.current) {
        Sentry.addBreadcrumb({
          category: "dd-report.fetch",
          level: "warning",
          message: `${title} — still failing`,
          data: { caseAlias, ...data },
        });
        return;
      }
      Sentry.captureException(new Error(title), {
        tags: {
          area: "dd-report",
          dataSource: "report",
          errorCode: data.errorCode ?? "UNKNOWN",
          httpStatus: data.status ? String(data.status) : "unknown",
        },
        extra: { caseAlias, backendTraceId: data.traceId },
      });
      sentryReportedRef.current = true;
    };

    const run = async () => {
      if (cancelled) return;
      const result = await getReportManifestRef.current(caseAlias, language ?? undefined);
      if (cancelled) return;

      if (isErr(result)) {
        const status = result.error.status;
        const code = result.error.errorCode;

        if (status === 404) {
          if (caseStatusRef.current === DueDiligenceCaseStatuses.pending_payment) {
            setState("ready");
            return;
          }
          retryTimerRef.current = setTimeout(run, DEFAULT_RETRY_SECONDS * 1000);
          return;
        }

        reportErrorToSentry(
          `Report manifest fetch failed: ${code ?? "no code"}`,
          { status, errorCode: code, traceId: result.error.traceId },
        );
        setHasFailedAtLeastOnce(true);
        retryTimerRef.current = setTimeout(run, ERROR_RETRY_SECONDS * 1000);
        return;
      }

      const response = result.value;
      if (response.status === "pending" || !response.data) {
        // No blob yet (e.g. just-created case before the seed). For pending_payment
        // show the locked state; otherwise keep polling until the manifest appears.
        if (caseStatusRef.current === DueDiligenceCaseStatuses.pending_payment) {
          setState("ready");
          return;
        }
        const seconds = response.retryAfterSeconds || DEFAULT_RETRY_SECONDS;
        retryTimerRef.current = setTimeout(run, seconds * 1000);
        return;
      }

      setManifest(response.data);
      initializeExcludedPdfSections(response.data.hiddenSections ?? []);
      setDataVersion((v) => v + 1);
      setHasFailedAtLeastOnce(false);
      sentryReportedRef.current = false;
      setState("ready");
      // Section 1 must render on entry — fetch its content eagerly (the rest is
      // lazy, driven by the IntersectionObserver below).
      ensureSectionRef.current("1");
    };

    run();
    return () => {
      cancelled = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [caseAlias, refetchKey, language]);

  // Per-section lazy fetch + retry chain
  // The backend returns a pay-aware effective status per section, so the polling
  // rule is simply: keep re-requesting while it is not completed. Sections that
  // are final for the current payment state come back `completed` and stop.
  const clearRetry = (number: string) => {
    const timer = retryTimersRef.current[number];
    if (timer) {
      clearTimeout(timer);
      delete retryTimersRef.current[number];
    }
  };

  const scheduleRetry = (number: string) => {
    // Don't reset an already-armed countdown (the request chain is already there).
    if (retryTimersRef.current[number]) return;
    const seconds = isPaidRef.current ? SECTION_RETRY_SECONDS_PAID : SECTION_RETRY_SECONDS_PREPAID;
    retryTimersRef.current[number] = setTimeout(() => {
      delete retryTimersRef.current[number];
      fetchSectionRef.current(number);
    }, seconds * 1000);
  };

  const fetchSection = async (number: string) => {
    if (!caseAlias) return;
    if (inFlightRef.current.has(number)) return;
    inFlightRef.current.add(number);
    const result = await getReportNumberedSectionRef.current(caseAlias, number, language ?? undefined);
    inFlightRef.current.delete(number);

    // Readiness is the envelope status: 'ready' (body present → show, stop) vs
    // 'pending' (no body → spinner, keep polling). On error, retry too.
    if (!isErr(result) && result.value.status === "ready" && result.value.data) {
      // Only this section's state changes — its key stays stable, so the rest of
      // the report is not remounted (don't bump dataVersion here).
      setSectionContent((prev) => ({ ...prev, [number]: result.value.data }));
      clearRetry(number);
      return;
    }
    scheduleRetry(number);
  };
  fetchSectionRef.current = fetchSection;

  const ensureSection = (number: string) => {
    // A countdown is already armed for this section → leave the chain in place.
    if (retryTimersRef.current[number]) return;
    if (inFlightRef.current.has(number)) return;
    if (sectionContentRef.current[number]) return; // already loaded (ready) — done
    fetchSection(number);
  };
  ensureSectionRef.current = ensureSection;

  // MIKE-309: only one section is ever mounted at a time now (the active
  // one), so there's nothing to scroll-ahead-prefetch — opening a section
  // (openSection) already ensures its content is fetched. Just clear any
  // armed retry chains on unmount.
  useEffect(() => {
    return () => {
      Object.keys(retryTimersRef.current).forEach((n) => clearRetry(n));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!caseAlias) return;
    getReportFiles(caseAlias).then((result) => {
      if (!isErr(result)) setFilesMap(result.value.files);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseAlias]);

  // A slow pre-payment task (e.g. the cadastral floor-distribution PDF) can
  // create its document AFTER the initial files fetch. When a freshly loaded
  // section references a file we haven't signed yet, refresh the files map so
  // the link resolves on its own — no full page reload needed. Guarded so we
  // never re-sign (and re-flicker the images) when nothing is missing.
  useEffect(() => {
    if (!caseAlias) return;
    const referenced = new Set<string>();
    const re = /\{\{file:([^}]+)\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(JSON.stringify(sectionContent))) !== null) referenced.add(m[1]);
    const hasUnresolved = [...referenced].some((id) => !filesMapRef.current[id]);
    if (!hasUnresolved) return;
    getReportFiles(caseAlias).then((result) => {
      if (!isErr(result)) setFilesMap(result.value.files);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionContent, caseAlias]);

  // MIKE-309: sections are no longer a single scrollable page — clicking a
  // section in the sidebar replaces the whole content area with just that
  // section, fetching it on demand. `ensureSection` is already the browser
  // cache: it skips the request entirely if that section's content was
  // already loaded (sectionContentRef), so re-opening an already-open
  // section is free.
  // `behavior` is how it lands on the section's top: instant for the sidebar,
  // smooth for a link that names the section (the chatbot's sources).
  const openSection = useCallback((sectionId: string, behavior: ScrollBehavior = "auto") => {
    const m = /^section-(\d+)$/.exec(sectionId);
    if (!m) return;
    const number = parseInt(m[1], 10);
    if (!isPreviewSectionVisible(number)) return;
    setActiveSectionNumber(number);
    ensureSectionRef.current(String(number));
    window.scrollTo({ top: 0, behavior });
  }, [isPreviewSectionVisible]);

  const scrollToBlock = useCallback((blockId: string) => {
    const el = blockRefs.current[blockId];
    if (!el) return false;
    const subheader = document.querySelector("[data-subheader]") as HTMLElement | null;
    const offset = subheader ? subheader.getBoundingClientRect().bottom : 0;
    const top = el.getBoundingClientRect().top + window.scrollY - offset - 8;
    window.scrollTo({ top, behavior: "smooth" });
    return true;
  }, []);

  useEffect(() => {
    const onScrollTo = (sectionId: string) => openSection(sectionId);
    const onToggle = (sectionId: string) =>
      setEnabledSections((prev) => ({ ...prev, [sectionId]: prev[sectionId] === false }));
    const onScrollToBlock = (blockId: string) => scrollToBlock(blockId);
    const onToggleBlock = (blockId: string) =>
      setEnabledBlocks((prev) => ({ ...prev, [blockId]: prev[blockId] === false }));
    const onRenderError = () => {
      if (renderErrorRetryRef.current) return;
      renderErrorRetryRef.current = setTimeout(() => {
        renderErrorRetryRef.current = null;
        isSilentRefetchRef.current = true;
        setRefetchKey((k) => k + 1);
      }, 5000);
    };
    eventBus.on("REPORT_SCROLL_TO_SECTION", onScrollTo);
    // The renderer moved to the shared package and fires on its own bus
    // (it cannot reach this app's), so the page listens on both.
    reportEventBus.on("REPORT_SCROLL_TO_SECTION", onScrollTo);
    eventBus.on("REPORT_TOGGLE_SECTION", onToggle);
    eventBus.on("REPORT_SCROLL_TO_BLOCK", onScrollToBlock);
    eventBus.on("REPORT_TOGGLE_BLOCK", onToggleBlock);
    eventBus.on("REPORT_SECTION_RENDER_ERROR", onRenderError);
    return () => {
      eventBus.off("REPORT_SCROLL_TO_SECTION", onScrollTo);
      reportEventBus.off("REPORT_SCROLL_TO_SECTION", onScrollTo);
      eventBus.off("REPORT_TOGGLE_SECTION", onToggle);
      eventBus.off("REPORT_SCROLL_TO_BLOCK", onScrollToBlock);
      eventBus.off("REPORT_TOGGLE_BLOCK", onToggleBlock);
      eventBus.off("REPORT_SECTION_RENDER_ERROR", onRenderError);
      if (renderErrorRetryRef.current) clearTimeout(renderErrorRetryRef.current);
    };
  }, [openSection, scrollToBlock]);

  useEffect(() => { eventBus.emit("REPORT_ENABLED_SECTIONS", enabledSections); }, [enabledSections]);
  useEffect(() => { eventBus.emit("REPORT_ENABLED_BLOCKS", enabledBlocks); }, [enabledBlocks]);

  useEffect(() => {
    if (!manifest?.sections) return;
    // Nav/outline comes from the manifest (block outline = number + title), so the
    // sidebar is complete before any section content is lazy-loaded.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const outline: ReportOutline = (manifest.sections as Array<any>).filter((section) => isPreviewSectionVisible(section.number)).map((section) => ({
      id: `section-${section.number}`,
      // The manifest carries section numbers as strings; the outline contract
      // types them as numbers and the sidebar compares them numerically (e.g.
      // to lift §1 out of the numbered list), so coerce here rather than
      // sprinkling Number() over every consumer.
      number: Number(section.number),
      displayNumber: Number(section.displayNumber ?? section.number),
      title: section.title || "",
      status: sectionContent[String(section.number)] ? (section.status === SectionDataStatuses.blocked ? SectionDataStatuses.blocked : SectionDataStatuses.completed) : section.status, requiredLevel: section.requiredLevel, navTitle: section.navTitle, verdict: section.verdict, showVerdict: section.showVerdict,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      blocks: (section.blocks || []).map((block: any) => ({
        id: blockDomId(block.number),
        number: block.number,
        title: block.title || "",
      })),
    }));
    eventBus.emit("REPORT_OUTLINE", outline);
  }, [manifest, isPreviewSectionVisible, sectionContent]);

  useEffect(() => {
    if (!ddCase) return;
    const m = manifest?.meta || {};
    const caseMeta: ReportCaseMeta = {
      tier: ddCase.tier,
      title: m.propertyName || m.address || ddCase.name || "",
      address: m.address || ddCase.propertyAddress || "",
    };
    eventBus.emit("REPORT_CASE_META", caseMeta);
  }, [ddCase, manifest]);

  useEffect(() => {
    eventBus.emit("REPORT_SECTION_CHANGED", `section-${activeSectionNumber}`);
  }, [activeSectionNumber]);

  useEffect(() => {
    if (!manifest?.sections?.length) return;
    if (isPreviewSectionVisible(activeSectionNumber) && activeSectionNumber !== 0) return;
    const firstVisible = manifest.sections.find((section: { number: string | number }) => isPreviewSectionVisible(section.number));
    if (firstVisible) openSection(`section-${firstVisible.number}`);
    else setActiveSectionNumber(0);
  }, [manifest, activeSectionNumber, isPreviewSectionVisible, openSection]);

  // MIKE-329: `?notes=N` is how the case detail page hands a note back to the
  // report — open that section and its panel. Once only, and the param is
  // dropped afterwards so a reload does not reopen the panel on top of
  // whatever the reader has navigated to since.
  useEffect(() => {
    if (notesDeepLinkDone.current || !manifest?.sections?.length) return;
    const requested = Number(searchParams.get("notes"));
    if (!requested) return;

    notesDeepLinkDone.current = true;
    openSection(`section-${requested}`);
    setNotesSectionNumber(requested);
    searchParams.delete("notes");
    setSearchParams(searchParams, { replace: true });
  }, [manifest, openSection, searchParams, setSearchParams]);

  // `#section-N` / `#block-N-M` in the URL — the DD chatbot's source links —
  // open that section and scroll to the block. After the effects above, so it
  // wins over the default first section.
  useReportAnchorNavigation({ sections: manifest?.sections, openSection, scrollToBlock });

  // Fail-closed: if the case failed to load (ddCase is null, e.g. NOT_AUTHORIZED
  // or a network error) we must NOT assume it's paid, otherwise the preview would
  // render unblurred. Only treat as paid when we actually have the case.
  const isPaid = ddCase ? ddCase.status !== DueDiligenceCaseStatuses.pending_payment : false;
  useEffect(() => { isPaidRef.current = isPaid; }, [isPaid]);
  // §1's cockpit shows its own map, so the masthead one is redundant once it renders.
  // Notion's trick: finishing a selection inside the report body pops the same
  // sheet the right button gives, so copying or annotating what you just
  // highlighted is one gesture instead of two. The read is deferred a tick
  // because on mouseup the browser has not always committed the selection yet.
  const openMenuOnSelection = useCallback(() => {
    window.setTimeout(() => {
      const selection = readSelectionInside(reportWrapperRef.current);
      if (!selection) return;
      setNotesMenuAnchor({
        // Under the selection, left-aligned with it, so the sheet never covers the text it acts on.
        x: selection.rect.left,
        y: selection.rect.bottom + 8,
        sectionNumber: activeSectionNumber,
        selectedText: selection.text,
      });
    }, 0);
  }, [activeSectionNumber]);

  // MIKE-322: the property masthead (title/address/map) is only meaningful on
  // the executive summary — every other section shows the "Volver al resumen
  // ejecutivo" pill + section header instead, per the design.
  const isExecutiveSummaryActive = activeSectionNumber === 1 && isPreviewSectionVisible(1);
  const canExport = ddCase?.status === DueDiligenceCaseStatuses.completed;
  const { isPayingNow, handlePayNow } = useDueDiligencePayment(caseAlias, ddCase?.tier);
  const canUpsell = !!ddCase && isPaid && ddCase.status !== "archived" && !!ddCase.tier && ddCase.tier !== "premium";

  const handleLanguageChange = async (newLanguage: string | null) => {
    setLanguage(newLanguage);
    if (!newLanguage || !caseAlias) return;
    const result = await translateReport(caseAlias, newLanguage);
    if (isErr(result)) {
      eventBus.emit("NOTIFICATION", Notification.buildNotification("error", t("dueDiligence.finalReport.translationError")));
    }
  };

  useEffect(() => {
    if (!canUpsell || pricing) return;
    getDueDiligencePricing().then((result) => {
      if (isErr(result)) return;
      setPricing(result.value.pricing);
      setCreditCosts(result.value.creditCosts);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUpsell]);

  // The report's upsell CTAs name the level they unlock; one that names none
  // goes to the tier right above the current one. Either way the upgrade modal
  // confirms it first, with credits for subscribers.
  const handleReportUpsell = (level: number | null) => {
    if (!ddCase?.tier) return;
    const TIER_ORDER = dueDiligenceTierSchema.options;
    const next = TIER_ORDER[TIER_ORDER.indexOf(ddCase.tier) + 1];
    const named = level ? TIER_ORDER[level - 1] : undefined;
    const target = named && TIER_ORDER.indexOf(named) > TIER_ORDER.indexOf(ddCase.tier) ? named : next;
    if (target) setUpsellTier(target);
  };

  // Upgraded with credits: the case has its new tier and the report regenerates
  const handleUpgraded = (upgradedCase: DueDiligenceCase) => {
    setDdCase(upgradedCase);
    setRefetchKey((key) => key + 1);
  };
  // Structure comes from the manifest; heavy block content from lazy-loaded
  // sectionContent. A section's blocks are only fed to the renderer once its
  // content has actually loaded (manifest entries carry the outline, not content).
  const meta = manifest?.meta || {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const manifestSections: Array<any> = manifest?.sections || [];
  const isLoaded = (number: string | number) => !!sectionContent[String(number)];
  const sections = manifestSections.map((entry) => {
    const content = sectionContent[String(entry.number)];
    // A section is "loaded" only when the backend served it as ready (pay-aware);
    // until then content is absent and the open branches show a spinner.
    let blocks = content?.blocks ?? [];

    return {
      number: entry.number,
      displayNumber: entry.displayNumber ?? entry.number,
      title: entry.title,
      status: entry.status, lockedContent: entry.lockedContent, verdict: entry.verdict, showVerdict: entry.showVerdict, requiredLevel: entry.requiredLevel,
      blocks,
      synth: content?.synth,
      callouts: content?.callouts,
      sources: content?.sources,
    };
  });

  const { notesBySection, add: addNote, rewrite: rewriteNote, remove: removeNote } =
    useDueDiligenceReportNotes(caseAlias);
  const openNotesSection = manifestSections.find((s) => Number(s.number) === notesSectionNumber);

  // MIKE-317: sections the paid tier does not unlock are locked teasers here —
  // an upsell card on screen, and nothing at all in the PDF. They must not be
  // offered in the export dialog either, or the client's PDF would be built
  // around sections nobody can read.
  const exportableSections = manifestSections.filter((s) => s.status !== SectionDataStatuses.blocked);

  // The count stays stable as content streams in: derive it from the manifest
  // rather than from the (progressively loaded) rendered sections.
  const visibleManifest = manifestSections.filter(
    (s) => enabledSections[`section-${s.number}`] !== false && isPreviewSectionVisible(s.number),
  );
  const sectionsCount = visibleManifest.length;

  const currentSection = manifestSections.find((s) => Number(s.number) === activeSectionNumber) || {
    number: 1,
    displayNumber: 1,
    title: t(REPORT_TITLE_KEY),
  };

  // Section header (MIKE-322): "Volver al resumen ejecutivo" pill + icon
  // badge / group overline / bold title / verdict pill / fiab tag, replacing
  // the old plain "Sección N / Title" pair — matches the design's own
  // section-drawer header. Section 1 (the executive summary itself) gets no
  // header here: it has its own dedicated top treatment, and a "back to
  // summary" button makes no sense while already on it. The urbanistic
  // report keeps the old plain header — this design pass didn't cover it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // Warns, in the internal view only, that this section is one the export
  // dialog left out of the client's PDF — otherwise the exclusion is invisible
  // until the PDF comes out without it. In the client preview the section is
  // not rendered at all, so there is nothing to warn about.
  const renderPdfExclusionNotice = (number: string | number) => {
    if (clientPreview || !excludedPdfSections.includes(String(number))) return null;
    return (
      <PdfExclusionNotice>
        <FiEyeOff size={18} />
        <PdfExclusionText>
          <Trans i18nKey="dueDiligence.finalReport.exportModal.excludedNoticeText" components={{ 1: <strong /> }} />
        </PdfExclusionText>
        <PdfExclusionAction
          onClick={() => setExcludedPdfSections((prev) => prev.filter((n) => n !== String(number)))}
        >
          {t("dueDiligence.finalReport.exportModal.excludedNoticeAction")}
        </PdfExclusionAction>
      </PdfExclusionNotice>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderSectionHeader = (section: any) => {
    const sectionNumber = Number(section.number);
    const exclusionNotice = renderPdfExclusionNotice(section.number);
    if (sectionNumber === 1) return exclusionNotice;


    const status = section.verdict ?? designSectionStatus(sectionNumber);
    const groupKey = section.requiredLevel === undefined ? SECTION_GROUP_KEYS[sectionNumber] : undefined;
    const icon = LEGAL_FISCAL_SECTION_ICONS[sectionNumber];
    const fiabKey = ORIENTATIVE_SECTION_NUMBERS.has(sectionNumber) ? "orientativo" : "verificado";
    const StatusIcon = status === "favorable" ? FiCheck : status === "reservas" ? FiAlertTriangle : FiAlertCircle;
    // Design README: "fuentes" (Anexo) shows no status badge/fiab tag — its
    // content isn't a verified/orientativo claim, just a document list.
    // A section whose data has not landed yet has nothing to be favorable or
    // verified about: the pill would sit right on top of "generating this
    // section". Design README: "fuentes" (Anexo) shows no status badge/fiab
    // tag either — its content isn't a verified claim, just a document list.
    const showStatus =
      !isSectionAwaitingData(section.status) &&
      (section.showVerdict ?? !NO_STATUS_SECTION_NUMBERS.has(sectionNumber));

    return (
      <>
        {exclusionNotice}
        {isPreviewSectionVisible(1) && <BackToSummaryButton onClick={() => openSection("section-1")}>
          <FiChevronLeft size={15} /> {t("dueDiligence.finalReport.sectionHeader.backToSummary")}
        </BackToSummaryButton>}
        <SectionHeaderRow>
          {icon && (
            <SectionHeaderIconBadge $status={status}>
              {cloneElement(icon, { size: 20 })}
            </SectionHeaderIconBadge>
          )}
          <div>
            <SectionHeaderOverline>
              {sectionLabel(section.displayNumber ?? section.number)}
              {groupKey && ` · ${t(`dueDiligence.finalReport.sectionHeader.group.${groupKey}`)}`}
            </SectionHeaderOverline>
            <SectionHeaderTitle>{section.title}</SectionHeaderTitle>
            {showStatus && (
              <SectionHeaderMetaRow>
                <SectionStatusPill $status={status}>
                  <StatusIcon size={11} /> {t(`dueDiligence.finalReport.sectionHeader.status.${status}`)}
                </SectionStatusPill>
                <SectionFiabTag>{t(`dueDiligence.finalReport.sectionHeader.fiab.${fiabKey}`)}</SectionFiabTag>
              </SectionHeaderMetaRow>
            )}
          </div>
          <SectionNotesMarker
            count={notesBySection.get(sectionNumber)?.length ?? 0}
            awake={markersAwake}
            onClick={() => setNotesSectionNumber(sectionNumber)}
          />
        </SectionHeaderRow>
      </>
    );
  };

  // Every numbered section starts with the same four-item synth strip. The
  // executive summary is the sole exception: it has its own cockpit header.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderSynthStrip = (section: any) =>
    Number(section.number) === 1 ? null : renderSynthStripBase(section.synth);

  const renderSectionSources = (section: any) => section.sources ? (
    <SectionSources>
      <FiFileText size={13} />
      <span>{renderWithFileRefs(section.sources, filesMap, demoCitations)}</span>
    </SectionSources>
  ) : null;

  // Spinner placeholder for an "open" section that is not loaded yet or still
  // pending. Stays registered with the observer so its content keeps streaming in.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderLoadingSection = (section: any) => (
    <SectionErrorBoundary key={`${section.number}-${dataVersion}`} sectionName={`section-${section.number}`}>
      <SectionWrapper id={sectionDomId(section.number)} $visible>
        {renderSectionHeader(section)}
        {renderSynthStrip(section)}
        {renderSectionCallouts(section.callouts)}
        <SectionLoadingState>
          <Spinner /> {t("dueDiligence.finalReport.loadingSection")}
        </SectionLoadingState>
      </SectionWrapper>
    </SectionErrorBoundary>
  );

  return (
    <CitationsContext.Provider value={demoCitations}>
    <ReportPageContainer>
      <ReportSubheader
        currentSection={{
          number: currentSection.displayNumber ?? currentSection.number,
          title: currentSection.title,
          icon: "",
        }}
        totalSections={sectionsCount}
        onExport={canExport ? () => setShowExportModal(true) : undefined}
        tier={isPaid ? ddCase?.tier : null}
        pricing={pricing}
        creditCosts={isSubscriber ? creditCosts : null}
        onUpgrade={canUpsell ? setUpsellTier : undefined}
        language={language}
        onLanguageChange={handleLanguageChange}
      />

      {showExportModal && caseAlias && (
        <ExportPdfModal
          key={caseAlias}
          sections={exportableSections}
          excluded={excludedPdfSections}
          setExcluded={setExcludedPdfSections}
          clientPreview={clientPreview}
          onClientPreviewChange={setClientPreview}
          onClose={() => setShowExportModal(false)}
          onExport={async (excludedSections) => {
            const result = await exportPdf(caseAlias, `informe-${caseAlias}.pdf`, language ?? undefined, excludedSections);
            if (isErr(result)) throw new Error("PDF export failed");
          }}
        />
      )}

      <ReportWrapper
        onContextMenu={(event) => {
          event.preventDefault();
          setNotesMenuAnchor({
            x: event.clientX,
            y: event.clientY,
            sectionNumber: activeSectionNumber,
            selectedText: readSelectionInside(reportWrapperRef.current)?.text,
          });
        }}
        onMouseUp={openMenuOnSelection}
        onMouseMove={wakeMarkers}
        ref={reportWrapperRef}
      >
        {clientPreview && (
          <div role="status" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", marginBottom: 20, borderRadius: 10, background: "#eef5f1", color: "#14323b" }}>
            <span>{t("dueDiligence.finalReport.exportModal.previewActive")}</span>
            <button type="button" onClick={() => setClientPreview(false)} style={{ border: "1px solid #d8e3dc", borderRadius: 8, padding: "6px 12px", background: "white", cursor: "pointer", color: "inherit" }}>
              {t("dueDiligence.finalReport.exportModal.exitPreview")}
            </button>
          </div>
        )}
        {clientPreview && visibleManifest.length === 0 && (
          <p>{t("dueDiligence.finalReport.exportModal.emptyPreview")}</p>
        )}
        {/* MIKE-322: the property header (title/address/map) only belongs on
            the executive summary — every other section replaces it with the
            "Volver al resumen ejecutivo" pill + the section header below,
            same as the design's own section-drawer view. */}
        {isExecutiveSummaryActive && (
          <>
            <ReportHeader>
              <CockpitHeaderRow>
                <CockpitHeaderLeft>
                  {meta.propertyType && (
                    <CockpitBadge>
                      <CockpitBadgeDot />
                      {meta.propertyType}
                    </CockpitBadge>
                  )}
                  <CockpitTitle>{meta.propertyName || meta.address || ""}</CockpitTitle>
                  <CockpitSubtitle>
                    {[meta.address, ddCase?.cadastralReference && cadastralRefsText(ddCase.cadastralReference)]
                      .filter(Boolean)
                      .join(" · ")}
                  </CockpitSubtitle>
                </CockpitHeaderLeft>
                <CockpitHeaderRight>
                  <CockpitOverline>{t("dueDiligence.finalReport.cockpit.headerOverline")}</CockpitOverline>
                  {ddCase?.alias && <CockpitRef>{ddCase.alias}</CockpitRef>}
                  <CockpitDate>{formatLongDate(meta.date)}</CockpitDate>
                  <SectionNotesMarker
                    count={notesBySection.get(1)?.length ?? 0}
                    awake={markersAwake}
                    onClick={() => setNotesSectionNumber(1)}
                  />
                </CockpitHeaderRight>
              </CockpitHeaderRow>
            </ReportHeader>

            <HeaderDivider />
          </>
        )}


        {state === "loading" && (
          <SectionLoadingState>
            <Spinner /> {hasFailedAtLeastOnce
              ? t("dueDiligence.finalReport.loadFailed")
              : t("dueDiligence.finalReport.loadingReport")}
          </SectionLoadingState>
        )}

        {state === "ready" && !isPaid && sections.length === 0 && (
          <LockedSectionCard>
            <LockedSectionIcon><FiLock size={16} /></LockedSectionIcon>
            <LockedSectionText>
              {t("dueDiligence.finalReport.lockedBeforePayment")}
            </LockedSectionText>
            <LockedSectionCTA onClick={handlePayNow} disabled={isPayingNow}>
              <FiUnlock size={13} /> {isPayingNow ? t("dueDiligence.finalReport.redirecting") : t("dueDiligence.finalReport.unlockFullReport")}
            </LockedSectionCTA>
          </LockedSectionCard>
        )}

        {/* MIKE-309: only the active/open section renders — clicking a section in the
            sidebar (openSection) swaps this whole area instead of scrolling to it. */}
        {state === "ready" &&
          sections
            .filter((section) => Number(section.number) === activeSectionNumber && enabledSections[`section-${section.number}`] !== false && isPreviewSectionVisible(section.number))
            .map((section) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const sectionBlocks = (section.blocks || []) as Array<any>;
              // section.number arrives as a string ("5") from the backend; the
              // comparisons and Sets are numeric, so we coerce it.
              const sectionNumber = Number(section.number);

              if (section.status === SectionDataStatuses.blocked) {
                const content = section.lockedContent;
                return (
                  <SectionWrapper key={section.number} id={sectionDomId(section.number)} $visible>
                    {renderSectionHeader(section)}
                    <TierLockedCard>
                      <TierLockedIcon><FiLock size={22} /></TierLockedIcon>
                      {content?.title && <TierLockedTitle>{content.title}</TierLockedTitle>}
                      {content?.description && <TierLockedText>{content.description}</TierLockedText>}
                      {content?.availability && (
                        <TierLockedAvailability>
                          {[content.availability, content.price].filter(Boolean).join(" · ")}
                        </TierLockedAvailability>
                      )}
                    </TierLockedCard>
                  </SectionWrapper>
                );
              }

              // ── Visible section: 2 — real data, no blur ────────────────
              if (!isPaid && PREVIEW_VISIBLE_SECTIONS.has(sectionNumber)) {
                if (!isLoaded(section.number)) return renderLoadingSection(section);
                return (
                  <SectionErrorBoundary key={`${section.number}-${dataVersion}`} sectionName={`section-${section.number}`}>
                    <SectionWrapper id={sectionDomId(section.number)} $visible>
                      {renderSectionHeader(section)}
                      {renderSynthStrip(section)}
                      {renderSectionCallouts(section.callouts)}
                      {sectionBlocks
                        .filter((block: { number: string }) => enabledBlocks[blockDomId(block.number)] !== false)
                        .map((block: { number: string; blurred?: boolean }, index: number) => {
                          const id = blockDomId(block.number);
                          const rendered = (
                            <BlockRenderer block={block} defaultOpen={index === 0} files={filesMap} citations={demoCitations} />
                          );
                          return (
                            <SectionErrorBoundary key={`${block.number}-${dataVersion}`} sectionName={`block-${block.number}`}>
                              <div id={id} ref={(el) => { blockRefs.current[id] = el; }}>
                                {block.blurred ? (
                                  <LockedBlockRelative>
                                    <BlurredContentWrapper>{rendered}</BlurredContentWrapper>
                                    <LockedOverlay>
                                      <LockedSectionIcon><FiLock size={16} /></LockedSectionIcon>
                                      <LockedSectionText>{t("dueDiligence.finalReport.blockLockedMessage")}</LockedSectionText>
                                      <LockedSectionCTA onClick={handlePayNow} disabled={isPayingNow}>
                                        <FiUnlock size={13} /> {isPayingNow ? t("dueDiligence.finalReport.redirecting") : t("dueDiligence.finalReport.unlockFullReport")}
                                      </LockedSectionCTA>
                                    </LockedOverlay>
                                  </LockedBlockRelative>
                                ) : rendered}
                              </div>
                            </SectionErrorBoundary>
                          );
                        })}
                      {renderSectionSources(section)}
                    </SectionWrapper>
                  </SectionErrorBoundary>
                );
              }

              // ── Blurred sections: 3-12 — blurred content + overlay ──────────
              if (!isPaid && PREVIEW_BLUR_SECTIONS.has(sectionNumber)) {
                return (
                  <SectionErrorBoundary key={`${section.number}-${dataVersion}`} sectionName={`section-${section.number}`}>
                    <SectionWrapper id={sectionDomId(section.number)} $visible>
                      {renderSectionHeader(section)}
                      {renderSynthStrip(section)}
                      {renderSectionCallouts(section.callouts)}
                      <LockedSectionRelative>
                        <BlurredContentWrapper>
                          {sectionBlocks.map((block, index) => (
                            <BlockRenderer key={block.number} block={block} defaultOpen={index === 0} files={filesMap} citations={demoCitations} />
                          ))}
                        </BlurredContentWrapper>
                        <LockedOverlay>
                          <LockedSectionIcon><FiLock size={16} /></LockedSectionIcon>
                          <LockedSectionText>{t("dueDiligence.finalReport.sectionLockedMessage")}</LockedSectionText>
                          <LockedSectionCTA onClick={handlePayNow} disabled={isPayingNow}>
                            <FiUnlock size={13} /> {isPayingNow ? t("dueDiligence.finalReport.redirecting") : t("dueDiligence.finalReport.unlockFullReport")}
                          </LockedSectionCTA>
                        </LockedOverlay>
                      </LockedSectionRelative>
                      {renderSectionSources(section)}
                    </SectionWrapper>
                  </SectionErrorBoundary>
                );
              }

              // ── Section 1: the cockpit, paid or not ────────────────────────
              // The essence block (1.1, "Viabilidad de la operación") renders
              // through ExecutiveSummaryCockpit — the sole thing shown for §1
              // now. Any other legacy block (e.g. 1.2 "Acciones previas a la
              // firma") is intentionally not rendered any more; see
              // project_mike308_kpi_backend_todo memory for what the backend
              // no longer needs to emit for this section.
              // Unpaid, the cockpit shows only what the case already knows —
              // the summary card and the property map — and the unlock CTA
              // closes the section. It used to blur an illustrative mockup
              // instead, which meant fabricating this property's verdict.
              if (sectionNumber === 1) {
                if (!isLoaded(section.number)) return renderLoadingSection(section);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const essenceBlock = sectionBlocks.find((b: any) => b.essence);
                return (
                  <SectionErrorBoundary key={`${section.number}-${dataVersion}`} sectionName={`section-${section.number}`}>
                    <SectionWrapper id={sectionDomId(section.number)} $visible>
                      {renderSectionHeader(section)}
                      {renderSynthStrip(section)}
                      {renderSectionCallouts(section.callouts)}
                      {essenceBlock && (
                        <ExecutiveSummaryCockpit
                          renderMap={({ lat, lng, zoom }) => <PropertyMap lat={lat} lng={lng} zoom={zoom} fill />}
                          renderHousingSimulator={(props) => <HousingSimulator {...props} />}
                          renderValuationSimulator={(props) => <ValuationSimulator {...props} />}
                          block={essenceBlock}
                          coordinates={meta.coordinates}
                          /* The coordinates ride with the cadastral data, so
                             §2 still being gathered means the map is on its
                             way rather than missing. */
                          mapPending={isSectionAwaitingData(
                            sections.find((s) => Number(s.number) === 2)?.status,
                          )}
                          address={meta.address}
                          propertyType={meta.propertyType}
                          kpis={essenceBlock.operationKpis}
                          panels={essenceBlock.operationPanels}
                          housingSimulator={essenceBlock.housingSimulator}
                          valuationSimulator={essenceBlock.valuationSimulator}
                          risks={essenceBlock.risks}
                          files={filesMap}
                          /* What the summary synthesizes: the other sections,
                             so §1 does not count itself. */
                          sectionsCount={sections.filter(s => s.status !== SectionDataStatuses.blocked && s.number !== "1").length}
                          tier={ddCase?.tier}
                          onUpsell={canUpsell ? handleReportUpsell : undefined}
                          upsellPending={upsellTier !== null}
                        />
                      )}
                      {!isPaid && (
                        <LockedSectionCard>
                          <LockedSectionIcon><FiLock size={16} /></LockedSectionIcon>
                          <LockedSectionText>{t("dueDiligence.finalReport.sectionLockedMessage")}</LockedSectionText>
                          <LockedSectionCTA onClick={handlePayNow} disabled={isPayingNow}>
                            <FiUnlock size={13} /> {isPayingNow ? t("dueDiligence.finalReport.redirecting") : t("dueDiligence.finalReport.unlockFullReport")}
                          </LockedSectionCTA>
                        </LockedSectionCard>
                      )}
                    </SectionWrapper>
                  </SectionErrorBoundary>
                );
              }

              // ── Default branch: paid (all sections) + unpaid section 13 ─────
              // These are "open": show a spinner until the section is served as
              // ready. The per-section retry chain keeps refetching meanwhile.
              if (!isLoaded(section.number)) return renderLoadingSection(section);

              return (
                <SectionErrorBoundary key={`${section.number}-${dataVersion}`} sectionName={`section-${section.number}`}>
                  <SectionWrapper id={sectionDomId(section.number)} $visible>
                    {renderSectionHeader(section)}
                    {renderSynthStrip(section)}
                    {renderSectionCallouts(section.callouts)}
                    {sectionBlocks
                      .filter((block: { number: string }) => enabledBlocks[blockDomId(block.number)] !== false)
                      .map((block: { number: string }, index: number) => {
                        const id = blockDomId(block.number);
                        return (
                          <SectionErrorBoundary key={`${block.number}-${dataVersion}`} sectionName={`block-${block.number}`}>
                            <div id={id} ref={(el) => { blockRefs.current[id] = el; }}>
                              <BlockRenderer block={block} defaultOpen={index === 0} files={filesMap} citations={demoCitations} />
                            </div>
                          </SectionErrorBoundary>
                        );
                      })}
                    {renderSectionSources(section)}
                  </SectionWrapper>
                </SectionErrorBoundary>
              );
            })}

        {state === "ready" && (
          <Disclaimer>
            {manifest?.legalNotice || t("dueDiligence.finalReport.disclaimer.defaultText")}
            <DisclaimerLink href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t("dueDiligence.finalReport.disclaimer.linkText")}</DisclaimerLink>
          </Disclaimer>
        )}

        {state === "ready" && (
          <FeedbackFooterPrompt feedbackStatus={feedbackStatus} onScoreClick={openFeedbackModalWithScore} />
        )}
      </ReportWrapper>

      <FeedbackGateOverlay
        feedbackStatus={feedbackStatus}
        feedbackConfig={feedbackConfig}
        isFeedbackModalOpen={isFeedbackModalOpen}
        isSubmittingFeedback={isSubmittingFeedback}
        initialNpsScore={initialNpsScore}
        onCloseModal={closeFeedbackModal}
        onSubmit={handleSubmitFeedback}
        onDismissAutoPrompt={handleDismissFeedbackPrompt}
      />

      <CitationSidePanel citations={demoCitations} />
      <NormaModal />
      {notesMenuAnchor && (
        <ReportNotesContextMenu
          anchor={notesMenuAnchor}
          onAddNote={() => {
            setNotesSectionNumber(notesMenuAnchor.sectionNumber);
            setNotesMenuAnchor(null);
          }}
          onClose={() => setNotesMenuAnchor(null)}
        />
      )}
      {openNotesSection && (
        <ReportNotesPanel
          sectionNumber={Number(openNotesSection.number)}
          sectionLabel={sectionLabel(openNotesSection.displayNumber ?? openNotesSection.number)}
          sectionTitle={openNotesSection.title}
          notes={notesBySection.get(Number(openNotesSection.number)) ?? []}
          onAdd={(content) => addNote(Number(openNotesSection.number), content)}
          onRewrite={rewriteNote}
          onRemove={removeNote}
          onClose={() => setNotesSectionNumber(null)}
        />
      )}
      {ddCase?.tier && (
        <UpsellModal
          isOpen={upsellTier !== null}
          onClose={() => setUpsellTier(null)}
          caseId={ddCase.id}
          currentTier={ddCase.tier}
          pricing={pricing}
          creditCosts={creditCosts}
          initialTier={upsellTier ?? undefined}
          onUpgraded={handleUpgraded}
        />
      )}
    </ReportPageContainer>
    </CitationsContext.Provider>
  );
};
